import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { AttributeResource } from '@df/catalog/attribute/attribute.resource';
import { OptionLabelPipe } from '@df/ui/common/option-label.pipe';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { UiComponent } from '@df/ui/ui.component';

export interface ISizeType {
    title: string;
    options: number[];
}

@Component({
    selector: 'profile-size-options',
    templateUrl: './profile-size-options.component.html',
    styleUrls: ['./profile-size-options.component.scss'],
    standalone: true,
    imports: [TranslatePipe, OptionLabelPipe],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProfileSizeOptionsComponent extends UiComponent implements OnChanges {
    @Input({ required: true })
    sizeType!: ISizeType;
    @Input()
    value?: number[];
    @Output()
    valueChange = new EventEmitter<number[]>();

    private attributeResource = inject(AttributeResource);
    protected selectedSizes: number[] = [];

    override ngOnInit(): void {
        this.selectedSizes = this.value || [];
    }

    /**
     * Get size options ordered according to attributeResource.assets.size.options
     */
    protected get orderedSizeOptions(): number[] {
        if (!this.sizeType?.options?.length) {
            return [];
        }

        const sizeAttribute = this.attributeResource.getExisting('size');
        const attributeOptions = sizeAttribute?.data?.options;

        if (!attributeOptions?.length) {
            return this.sizeType.options;
        }

        // Create a map of option values to their order index in the attribute resource
        const orderMap = new Map<number, number>();
        attributeOptions.forEach((option, index) => {
            orderMap.set(option.value, index);
        });

        // Sort the size type options based on their order in the attribute resource
        return [...this.sizeType.options].sort((a, b) => {
            const orderA = orderMap.get(a) ?? Infinity;
            const orderB = orderMap.get(b) ?? Infinity;
            return orderA - orderB;
        });
    }

    override ngOnChanges(changes: SimpleChanges): void {
        if (changes['value']) {
            this.selectedSizes = this.value || [];
        }
    }

    protected selectSize(size: number): void {
        const newSelectedSizes = [...this.selectedSizes];
        const index = newSelectedSizes.indexOf(size);

        if (index > -1) {
            // Remove size if already selected
            newSelectedSizes.splice(index, 1);
        } else {
            // Add size if not selected
            newSelectedSizes.push(size);
        }

        this.selectedSizes = newSelectedSizes;
        this.valueChange.emit(newSelectedSizes);
    }

    protected isSelected(size: number): boolean {
        return this.selectedSizes.includes(size);
    }
}
